import { NgClass } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewEncapsulation,
  type OnChanges,
  type SimpleChanges,
} from '@angular/core';
import {
  type MatCalendarCell,
  type MatCalendarUserEvent,
} from '@angular/material/datepicker';

/**
 * An internal component used to display calendar data in a table.
 */
@Component({
  selector: '[myre-calendar-body]',
  host: {
    class: 'mat-calendar-body',
    role: 'grid',
    'aria-readonly': 'true',
  },
  templateUrl: './myre-calendar-body.component.html',
  styleUrls: ['./myre-calendar-body.component.scss'],
  exportAs: 'matCalendarBody',
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [NgClass],
})
export class MyreCalendarBodyComponent implements OnChanges {
  // NEW: from Myre
  @Input()
  public selectedValues: number[];

  /** The cells to display in the table. */
  @Input()
  public rows: MatCalendarCell[][];

  /** The value in the table that corresponds to today. */
  @Input()
  public todayValue: number;

  /** The value in the table that is currently selected. */
  @Input()
  public selectedValue: number;

  /** The number of columns in the table. */
  @Input()
  public numCols: number = 7;

  /** The cell number of the active cell in the table. */
  @Input()
  public activeCell: number = 0;

  /**
   * The aspect ratio (width / height) to use for the cells in the table. This aspect ratio will be
   * maintained even as the table resizes.
   */
  @Input()
  public cellAspectRatio: number = 1;

  /** Emits when a new value is selected. */
  @Output()
  public readonly selectedValueChange: EventEmitter<
    MatCalendarUserEvent<number>
  > = new EventEmitter<MatCalendarUserEvent<number>>();

  /** The number of blank cells to put at the beginning for the first row. */
  public _firstRowOffset: number;

  /** Padding for the individual date cells. */
  public _cellPadding: string;

  /** Width of an individual cell. */
  public _cellWidth: string;

  public ngOnChanges(changes: SimpleChanges): void {
    const columnChanges = changes['numCols'];

    if (changes['rows'] || columnChanges) {
      this._firstRowOffset =
        this.rows && this.rows.length && this.rows[0].length
          ? this.numCols - this.rows[0].length
          : 0;
    }

    if (changes['cellAspectRatio'] || columnChanges || !this._cellPadding) {
      this._cellPadding = `${(50 * this.cellAspectRatio) / this.numCols}%`;
    }

    if (columnChanges || !this._cellWidth) {
      this._cellWidth = `${100 / this.numCols}%`;
    }
  }

  // NEW: from Myre
  public isSelected(value: number): boolean {
    return this.selectedValues.includes(value);
  }

  /** Called when a cell is clicked. */
  public _cellClicked(cell: MatCalendarCell, event: MouseEvent): void {
    if (cell.enabled) {
      this.selectedValueChange.emit({ event, value: cell.value });
    }
  }

  /** Returns whether a cell is active. */
  public _isActiveCell(rowIndex: number, colIndex: number): boolean {
    let cellNumber = rowIndex * this.numCols + colIndex;

    // Account for the fact that the first row may not have as many cells.
    if (rowIndex) {
      cellNumber -= this._firstRowOffset;
    }

    return cellNumber === this.activeCell;
  }
}
